/**
 * Sprint report model — a pure, React-free projection of a sprint's live state
 * into the flat {@link SprintReportData} the one-page PDF/print report renders.
 *
 * All figures come straight from the SAME deterministic engines the Sprint
 * Board already uses (insights, progress, {@link goalProgress}); this builder
 * only normalizes + labels them for a light, print-friendly layout. No I/O, no
 * React, no AI.
 */

import type { Task } from '../../tasks/types';
import type { GoalStatus } from '../../goals/types';
import { statusLabel as goalStatusLabel } from '../../goals/goalModel';
import { goalProgress, type SprintProgress } from '../engine/aggregation';
import type { SprintInsights } from '../insights/sprintInsights';

/** One goal row in the report's "Top goals" section. */
export interface ReportGoalRow {
  name: string;
  statusLabel: string;
  percentComplete: number;
}

/** The flat, display-ready payload the report layout renders. */
export interface SprintReportData {
  sprintName: string;
  goal?: string;
  /** "Day X of Y" or "Not scheduled". */
  dayLabel: string;
  /** Sprint lifecycle label (Planning / Active / Complete). */
  statusLabel: string;
  /** "On track" / "At risk". */
  healthLabel: string;
  healthy: boolean;
  percentComplete: number;
  committedEffortDays: number;
  completedEffortDays: number;
  remainingEffortDays: number;
  remainingCapacityDays: number;
  teamCapacityDays: number;
  memberCount: number;
  blockedCount: number;
  atRiskCount: number;
  /** Blocked tasks (longest-blocked first), capped for a single page. */
  blockers: { title: string; days: number; reason?: string }[];
  unplannedCount: number;
  unplannedEffortDays: number;
  /** Top goals advanced by the sprint's work, by committed effort. */
  topGoals: ReportGoalRow[];
  /** Short standup / digest highlights (already summarized upstream). */
  standupHighlights: string[];
  /** Local "generated on" label for the footer. */
  generatedAtLabel: string;
}

/** Inputs the page hands the builder — all already computed by the board hook. */
export interface SprintReportInput {
  sprintName: string;
  goal?: string;
  sprintStatusLabel: string;
  dayInfo: { day: number; total: number };
  insights: SprintInsights;
  progress: SprintProgress;
  memberCount: number;
  /** The sprint's tasks (for goal roll-ups). */
  tasks: Task[];
  /** Goal id → title/status lookup for the top-goals section. */
  goals: { id: string; title: string; status: GoalStatus }[];
  /** Pre-summarized standup / digest highlight lines. */
  standupHighlights: string[];
  now?: Date;
}

/** Max blockers / goals / highlights shown so the report stays one page. */
const MAX_BLOCKERS = 5;
const MAX_GOALS = 5;
const MAX_HIGHLIGHTS = 5;

/**
 * Builds the report payload. Top goals are the distinct goals linked to the
 * sprint's tasks, ranked by committed effort (desc), each with its human status
 * label and the completion % of its linked sprint tasks.
 */
export function buildSprintReportData(input: SprintReportInput): SprintReportData {
  const { insights, progress, dayInfo } = input;
  const now = input.now ?? new Date();

  const dayLabel =
    dayInfo.total > 0 ? `Day ${dayInfo.day} of ${dayInfo.total}` : 'Not scheduled';

  const goalById = new Map(input.goals.map((g) => [g.id, g]));
  const goalIds = Array.from(
    new Set(
      (input.tasks ?? [])
        .map((t) => t.goalId)
        .filter((g): g is string => !!g),
    ),
  );
  const topGoals: ReportGoalRow[] = goalIds
    .map((id) => {
      const gp = goalProgress(id, input.tasks);
      const g = goalById.get(id);
      return {
        committedDays: gp.committedDays,
        row: {
          name: g?.title || 'Untitled goal',
          statusLabel: g ? goalStatusLabel(g.status) : '—',
          percentComplete: gp.percentComplete,
        } as ReportGoalRow,
      };
    })
    .sort((a, b) => b.committedDays - a.committedDays)
    .slice(0, MAX_GOALS)
    .map((x) => x.row);

  return {
    sprintName: input.sprintName || 'Sprint',
    goal: input.goal?.trim() || undefined,
    dayLabel,
    statusLabel: input.sprintStatusLabel,
    healthLabel: insights.health === 'at_risk' ? 'At risk' : 'On track',
    healthy: insights.health !== 'at_risk',
    percentComplete: progress.percentComplete,
    committedEffortDays: insights.committedEffortDays,
    completedEffortDays: insights.completedEffortDays,
    remainingEffortDays: insights.remainingEffortDays,
    remainingCapacityDays: insights.remainingCapacityDays,
    teamCapacityDays: insights.teamCapacityDays,
    memberCount: input.memberCount,
    blockedCount: insights.blockedCount,
    atRiskCount: insights.atRiskCount,
    blockers: insights.blocked.slice(0, MAX_BLOCKERS).map((b) => ({
      title: b.title,
      days: b.days,
      reason: b.reason,
    })),
    unplannedCount: insights.unplannedCount,
    unplannedEffortDays: insights.unplannedEffortDays,
    topGoals,
    standupHighlights: (input.standupHighlights ?? [])
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, MAX_HIGHLIGHTS),
    generatedAtLabel: now.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}
